import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Tells a long-lived tab that a new build shipped.
 *
 * HQ is a single-page app: whatever JavaScript a tab loaded at open time is what
 * it keeps running, all day, no matter how many times the user navigates. Deploys
 * happen on every merge to main, so anyone who leaves HQ open keeps hitting bugs
 * that were already fixed — and it looks to everyone (including whoever shipped
 * the fix) as though the fix didn't work. That is exactly what happened with the
 * change-order step 3 freeze on 2026-08-17.
 *
 * Detection piggybacks on CRA's content-hashed bundle name: `index.html` always
 * points at `/static/js/main.<hash>.js`, and that hash changes on every build. We
 * remember the hash this tab is running, then re-fetch `index.html` and compare.
 * No build step, no version endpoint, nothing to keep in sync.
 *
 * The prompt never reloads on its own — a forced reload would throw away a
 * half-filled intake form, which is worse than running slightly stale code.
 */

const MAIN_BUNDLE_RE = /\/static\/js\/main\.[A-Za-z0-9]+\.js/;
const POLL_MS = 10 * 60 * 1000; // background sweep for tabs left open and untouched
const MIN_GAP_MS = 60 * 1000; // floor between checks, so tab-switching can't spam the server
const TOAST_ID = "hq-new-version";

/** The bundle this tab actually booted with, read off the live document. */
function currentBundle() {
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  const match = scripts.map(s => s.getAttribute("src") || "").find(src => MAIN_BUNDLE_RE.test(src));
  return match ? match.match(MAIN_BUNDLE_RE)[0] : null;
}

export default function NewVersionPrompt() {
  // Read once, at mount: this is the build the tab is running for its lifetime.
  const [bootedWith] = useState(currentBundle);
  const lastCheck = useRef(0);
  const notifiedFor = useRef(null);

  useEffect(() => {
    // Dev serves an unhashed bundle.js, so there is nothing to compare and
    // nothing to prompt about.
    if (process.env.NODE_ENV !== "production" || !bootedWith) return;

    let cancelled = false;

    const check = async () => {
      const now = Date.now();
      if (cancelled || now - lastCheck.current < MIN_GAP_MS) return;
      lastCheck.current = now;

      let html;
      try {
        // Bust both the HTTP cache and any proxy in front of it: prod serves
        // index.html with no Cache-Control at all, so a plain fetch can be
        // answered from cache and we'd never see the new build.
        const res = await fetch(`/index.html?_v=${now}`, { cache: "no-store" });
        if (!res.ok) return;
        html = await res.text();
      } catch {
        return; // offline or mid-deploy — try again on the next tick
      }
      if (cancelled) return;

      const shipped = html.match(MAIN_BUNDLE_RE)?.[0];
      // A miss means we got something that isn't our index.html (an nginx error
      // page, a captive portal). Staying quiet beats a false "update available".
      if (!shipped || shipped === bootedWith) return;
      // Already asked about this exact build and they dismissed it — let it be.
      if (notifiedFor.current === shipped) return;
      notifiedFor.current = shipped;

      toast("A new version of HQ is available", {
        id: TOAST_ID,
        description: "Refresh to pick up the latest fixes. Finish what you're doing first — nothing is lost by waiting.",
        duration: Infinity,
        action: {
          label: "Refresh",
          onClick: () => window.location.reload(),
        },
      });
    };

    // Coming back to the tab is the moment a refresh is cheapest — they aren't
    // mid-keystroke — so check then, and sweep on a timer for tabs nobody touches.
    const onFocus = () => { if (document.visibilityState === "visible") check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const timer = setInterval(check, POLL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(timer);
    };
  }, [bootedWith]);

  return null;
}
